import React, { ReactElement } from 'react';

import { Chart } from '../../../models';
import { State } from '../models';
import { MARKS } from '../../../constants/language';
import { getPx } from '../../../helpers';
import Bar from './Bar';
import Block from './Block';
import Carets from './Carets';
import Empty from './Empty';

interface MonitorProps {
  chart?: Chart;
  mpb: number;
  minTiming: number;
  maxTiming: number;
  count: number;
  state?: State;
  progress?: number;
  group?: number;
}

function Monitor({
  chart = [],
  mpb,
  minTiming,
  maxTiming,
  count,
  state,
  progress = 0,
  group,
}: MonitorProps): ReactElement {
  let safeChart = chart.filter((n) => n.sound.notation !== MARKS.caret);
  const getBarKey = (idx) => `PagesBeatbloxVisualizeMonitorBar${idx}`;
  const getBlockKey = (idx) => `PagesBeatbloxVisualizeMonitorBlock${idx}`;

  // Calculate notes
  safeChart = safeChart.filter((n) => n.sound.notation !== MARKS.rest);

  const shift = mpb / 2;
  const maxTrack = Math.max(...safeChart.map((n) => n.track)) + 2;
  const progressShifted = progress + shift;
  const minTimingShifted = minTiming + shift;

  safeChart = safeChart.map((n) => {
    const notation =
      n.sound.notation.length > 5
        ? `${n.sound.notation.slice(0, 5)}...`
        : n.sound.notation;

    return {
      ...n,
      track: n.track + 1,
      start: n.start + shift,
      ...(n.end && { end: n.end + shift }),
      sound: { ...n.sound, notation },
    };
  });

  // Calculate bars
  const bars = [...Array(count).keys()].map((k) => ({
    timing: Number(k) * mpb + shift,
    fuzzy: Number(k) !== 0,
  }));

  // Calculate carets
  const carets = chart
    .filter((n) => n.sound.notation === MARKS.caret)
    .map((n) => ({ ...n, start: n.start + shift }));

  // Styling
  const unitWidth = 108;
  const unitHeight = 30;
  const noteWidth = 25;
  const noteHeight = 25;
  const base = 6;
  const ratio = 0.8;
  const width = count * unitWidth;
  const height = Math.max(maxTrack, 2) * unitHeight;
  const maxPaddedTiming = maxTiming + mpb;
  const monitorStyle = {
    maxWidth: 'max(100vw, 100%)',
    height: getPx(height + 2 * base * ratio),
    overflowX: 'scroll' as const,
  };
  const containerStyle = {
    display: 'inline-block',
    position: 'relative' as const,
    width,
    height,
    marginTop: getPx(base * ratio),
  };

  return (
    <div style={monitorStyle}>
      <div style={containerStyle}>
        {safeChart.length === 0 && count < 2 && (
          <Empty height={noteHeight} state={state} />
        )}
        {bars.map((b, idx) => (
          <Bar
            key={getBarKey(idx)}
            height={height}
            timing={b.timing}
            minTiming={minTimingShifted}
            maxTiming={maxPaddedTiming}
            fuzzy={b.fuzzy}
            progress={progressShifted}
            group={group}
          />
        ))}
        {safeChart.map((n, idx) => (
          <Block
            key={getBlockKey(idx)}
            width={noteWidth}
            height={noteHeight}
            track={n.track}
            maxTrack={maxTrack}
            start={n.start}
            end={n.end}
            minTiming={minTimingShifted}
            maxTiming={maxPaddedTiming}
            sound={n.sound}
            progress={progressShifted}
            group={group}
          />
        ))}
        <Carets
          height={height}
          base={base}
          ratio={ratio}
          start={carets[0]?.start}
          end={carets[1]?.start}
          maxTiming={maxPaddedTiming}
        />
      </div>
    </div>
  );
}

export default Monitor;
